import { cn } from '@/shared/lib/cn'

/**
 * =============================================================================
 * LOGO ALTO PALERMO
 * =============================================================================
 * The horizontal presentation uses the official raster artwork from
 * `public/logo_alto_palermo.png`. The SVG remains available for compact
 * isotype and vertical variants that the horizontal asset cannot represent.
 *
 * El texto "Alto Palermo" y el descriptor son texto real (no trazos): usan la
 * tipografía y los colores del sistema, se seleccionan y se traducen.
 *
 * Reglas del manual respetadas aquí:
 *   · No deformar: el isotipo mantiene su relación de aspecto.
 *   · Tamaño mínimo del isotipo 55px en material de marketing; en el chrome de
 *     la app se usa más pequeño con la variante `isotype` (guía "sin
 *     desambiguador").
 *   · El oro es solo un acento: se puede desactivar con `tone="mono"`.
 * =============================================================================
 */

type LogoVariant = 'horizontal' | 'vertical' | 'isotype'
type LogoTone = 'full' | 'mono'

interface LogoProps {
  /** `horizontal` (isotipo + texto al lado), `vertical` (apilado) o solo `isotype`. */
  variant?: LogoVariant
  /** `full` = obelisco verde + estela dorada. `mono` = todo en `currentColor`. */
  tone?: LogoTone
  /** Muestra el descriptor "TRAVELS & TOURS". Se oculta en tamaños reducidos. */
  withDescriptor?: boolean
  /** Alto del isotipo en px. El texto escala en proporción. */
  size?: number
  className?: string
  /** Texto accesible. Por defecto "Alto Palermo". */
  title?: string
}

/** Isotipo: obelisco sobre un montículo con la estela orbitando la punta. */
function Isotype({ tone, size, title }: { tone: LogoTone; size: number; title: string }) {
  const gold = tone === 'full' ? 'var(--brand-gold)' : 'currentColor'

  return (
    <svg
      viewBox="0 0 128 148"
      width={(size * 128) / 148}
      height={size}
      role="img"
      aria-label={title}
      className="shrink-0"
    >
      {/* Estela: eco exterior fino */}
      <path
        d="M30 66 A 32 30 0 1 1 92 40"
        fill="none"
        stroke={gold}
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity={tone === 'full' ? 0.55 : 0.4}
      />
      {/* Estela: trazo principal alrededor de la punta */}
      <path
        d="M36 60 A 25 23 0 1 1 84 40"
        fill="none"
        stroke={gold}
        strokeWidth="5.5"
        strokeLinecap="round"
      />
      {/* Punta de avión al inicio de la estela */}
      <path d="M29 52 L42 57 L34 64 L36 58 Z" fill={gold} />

      {/* Obelisco */}
      <path d="M49 120 L55 40 L60 20 L65 40 L71 120 Z" fill="currentColor" />
      {/* Faceta lateral: da volumen */}
      <path d="M60 22 L65 40 L71 120 L64 120 Z" fill="currentColor" opacity="0.22" />
      {/* Ventana */}
      <path d="M55 52 L60 50 L60 58 L55 60 Z" fill="currentColor" opacity="0.35" />

      {/* Montículo */}
      <path d="M20 120 Q64 134 108 118 Q64 128 20 120 Z" fill="currentColor" />
    </svg>
  )
}

export function Logo({
  variant = 'horizontal',
  tone = 'full',
  withDescriptor = true,
  size = 36,
  className,
  title = 'Alto Palermo',
}: LogoProps) {
  if (variant === 'horizontal') {
    return (
      <img
        src="/logo_alto_palermo.png"
        alt={title}
        width={Math.round((size * 360) / 80)}
        height={size}
        className={cn('h-auto shrink-0 object-contain', className)}
      />
    )
  }

  if (variant === 'isotype') {
    return (
      <span className={cn('text-primary inline-flex', className)}>
        <Isotype tone={tone} size={size} title={title} />
      </span>
    )
  }

  const stacked = variant === 'vertical'

  return (
    <span
      className={cn(
        'text-primary inline-flex',
        stacked ? 'flex-col items-center gap-2 text-center' : 'items-center gap-3',
        className,
      )}
    >
      <Isotype tone={tone} size={size} title={title} />
      <span className={cn('flex flex-col', stacked ? 'items-center' : 'items-start')}>
        <span
          className="font-serif leading-[0.95] font-semibold tracking-tight"
          style={{ fontSize: size * 0.52 }}
        >
          Alto Palermo
        </span>
        {withDescriptor && (
          <span
            className="text-brand-gold font-sans font-medium uppercase"
            style={{ fontSize: size * 0.16, letterSpacing: '0.28em' }}
          >
            Travels&nbsp;&amp;&nbsp;Tours
          </span>
        )}
      </span>
    </span>
  )
}
